import LabeledSheet from 'LuxKit/LabeledSheet'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useListTruncator from 'hooks/useListTruncator'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import TourV2HighlightedInclusionItem from './TourV2HighlightedInclusionItem'
import type { TourV2AllInclusionsModalResult } from './TourVariations/TourV2AllInclusionsModal'
import TourV2AllInclusionsModal from './TourVariations/TourV2AllInclusionsModal'
import { partitionBy } from 'lib/array/arrayUtils'
import { getIsLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'
import { connect } from 'react-redux'

const HighlightedInclusionsContainer = styled(Group)`
  padding: ${rem(16)} ${rem(20)} ${rem(20)};
`

interface MappedStateProps {
  isLuxPlusEnabled: boolean
}

interface Props extends MappedStateProps {
  variation: App.Tours.TourV2OfferVariation
  onCheckAvailability: () => void
}

function TourV2HighlightedInclusions({ variation, onCheckAvailability, isLuxPlusEnabled }: Props) {
  const intl = useIntl()
  const [flashInclusions, nonFlashInclusions] = useMemo(() => partitionBy(variation.inclusionItems, inclusion => !!inclusion.isFlash), [variation.inclusionItems])
  const nonFlashFilteredInclusions = useMemo(() => {
    if (isLuxPlusEnabled) {
      return nonFlashInclusions
    }
    return nonFlashInclusions.filter(inclusion => !inclusion.isLuxPlusBonus)
  }, [nonFlashInclusions, isLuxPlusEnabled])

  const [bonusInclusions, inclusions] = useMemo(() => partitionBy(nonFlashFilteredInclusions, inclusion => !!inclusion.isLuxPlusBonus), [nonFlashFilteredInclusions])
  const inclusionShow = 3
  const [visible, hidden] = useListTruncator(inclusionShow, [...bonusInclusions, ...flashInclusions, ...inclusions])
  const showModal = useContext(ModalContext)
  const checkAvailabilityText = intl.formatMessage({
    id: 'Qa4GkXriBb',
    defaultMessage: 'Check availability',
    description: 'Button text to check tour availability on the tour offer detail page',
  })

  const openAllInclusionsModal = useCallback(async() => {
    const result = await showModal<TourV2AllInclusionsModalResult>(<TourV2AllInclusionsModal
      variation={variation}
      primaryActionText={checkAvailabilityText}
    />)
    if (result === 'primary-action') {
      onCheckAvailability()
    }
  }, [showModal, variation, onCheckAvailability, checkAvailabilityText])

  if (!visible.length) return null

  const limitedTimeLabel = intl.formatMessage({
    id: 'JCS4q546LY',
    defaultMessage: 'Limited time inclusions',
    description: 'Label for limited time tour inclusions on the tour offer detail page',
  })

  const whyYouLoveLabel = intl.formatMessage({
    id: '+iEU643fs6',
    defaultMessage: 'Why you’ll love this tour',
    description: 'Label for highlighted tour inclusions on the tour offer detail page',
  })

  return <LabeledSheet
    kind={isLuxPlusEnabled && bonusInclusions.length ? 'lux-plus' : (flashInclusions.length ? 'exclusive' : 'highlight')}
    label={flashInclusions.length && !bonusInclusions.length ? limitedTimeLabel : whyYouLoveLabel}
  >
    <HighlightedInclusionsContainer direction="vertical" gap={12}>
      <Group direction="vertical" gap={4}>
        {visible.map((inclusion) => <TourV2HighlightedInclusionItem
          key={inclusion.id}
          inclusion={inclusion}
        />)}
      </Group>
      {!!hidden.length && <div>
        <TextLink size="medium" onClick={openAllInclusionsModal}>
          {intl.formatMessage({
            id: 'CTFEUFZ64N',
            defaultMessage: '+{count} more inclusions',
            description: 'Link text showing number of additional inclusions in the tours section',
          }, { count: hidden.length })}
        </TextLink>
      </div>}
    </HighlightedInclusionsContainer>
  </LabeledSheet>
}

export default connect<MappedStateProps, {}, {}, App.State>((state) => {
  return {
    isLuxPlusEnabled: getIsLuxPlusToursEnabled(state),
  }
})(TourV2HighlightedInclusions)
